import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

export const useGetDivisionsQuery = (networkId) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_DIVISIONS, networkId ?? "all"],
    queryFn: () =>
      api.get(
        networkId
          ? `${API_ROUTES.GET_DIVISIONS}?networkId=${networkId}`
          : API_ROUTES.GET_DIVISIONS,
      ),
  });

  return { data: data?.data?.data ?? [], isPending, isError, error };
};

export const useGetDivisionByIdQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_DIVISIONS, id],
    queryFn: () => api.get(`${API_ROUTES.GET_DIVISIONS}/${id}`),
    enabled: !!id,
  });

  return { data: data?.data?.data ?? null, isPending, isError, error };
};

// The jurisdiction list is hardcoded on the backend and read-only here, so it
// never goes stale within a session.
export const useGetJurisdictionsQuery = () => {
  const { data, isPending } = useQuery({
    queryKey: [API_ROUTES.GET_JURISDICTIONS],
    queryFn: () => api.get(API_ROUTES.GET_JURISDICTIONS),
    staleTime: Infinity,
  });

  return { data: data?.data?.data ?? [], isPending };
};

const invalidate = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: [API_ROUTES.GET_DIVISIONS] });
  if (id) {
    queryClient.invalidateQueries({ queryKey: [API_ROUTES.GET_DIVISIONS, id] });
  }
};

export const useCreateDivisionMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isPending } = useMutation({
    mutationFn: (data) => api.post(API_ROUTES.GET_DIVISIONS, data),
    onSuccess: () => showToast("success", "Division created successfully"),
    // The backend refuses a jurisdiction another division already governs, and
    // that message is the useful one — surface it rather than a generic failure.
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to create division",
      ),
    onSettled: () => invalidate(queryClient),
  });

  return { mutate, isError, error, isPending };
};

export const useUpdateDivisionMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isPending } = useMutation({
    mutationFn: ({ id, ...data }) =>
      api.put(`${API_ROUTES.GET_DIVISIONS}/${id}`, data),
    onSuccess: () => showToast("success", "Division updated successfully"),
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to update division",
      ),
    onSettled: (_, __, variables) => invalidate(queryClient, variables?.id),
  });

  return { mutate, isError, error, isPending };
};

export const useDeleteDivisionMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isPending } = useMutation({
    mutationFn: (id) => api.delete(`${API_ROUTES.GET_DIVISIONS}/${id}`),
    onSuccess: () => showToast("success", "Division deleted"),
    // Refused while groups still point at it — that count is the message.
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to delete division",
      ),
    onSettled: () => invalidate(queryClient),
  });

  return { mutate, isError, error, isPending };
};
